import { useMutation } from "@tanstack/react-query"
import { z } from "zod"
import { throwKrakenErrorResponse } from "@krakentech/error-intl"
import { ERROR_CODE } from "@/constants/errorCode"
import { MarketName } from "@/constants/marketName"
import { graphql, type InputOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { getPaginatedNodesSchema } from "@/schemas/getPaginatedNodesSchema"

const QuotedProductSchema = z.object({
    id: z.number(),
    // The PaymentSchedule amount is truncate on Kraken's side, we reflect this behavior on the frontend to avoid confusion.
    // more details in this thread: https://octoenergy.slack.com/archives/C0APLEYSQ22/p1781167275519039?thread_ts=1781082094.926589&cid=C0APLEYSQ22
    monthlyAmount: z.number().transform((value) => Math.trunc(value)),
})

const ElectricityPaginatedQuotedSupplyPointSchema = z.object({
    quotedProducts: getPaginatedNodesSchema(QuotedProductSchema),
    quotedSupplyPoint: z.object({
        id: z.string(),
    }),
    supplyPoint: z.object({
        marketName: z.literal(MarketName.FRA_ELECTRICITY),
    }),
})

const GasPaginatedQuotedSupplyPointSchema = z.object({
    quotedProducts: getPaginatedNodesSchema(QuotedProductSchema),
    quotedSupplyPoint: z.object({
        id: z.string(),
    }),
    supplyPoint: z.object({
        marketName: z.literal(MarketName.FRA_GAS),
    }),
})

const QuotedSupplyPointSchema = z.union([
    ElectricityPaginatedQuotedSupplyPointSchema,
    GasPaginatedQuotedSupplyPointSchema,
])

function isElectricitySupplyPoint(
    quotedSupplyPoint: z.infer<typeof QuotedSupplyPointSchema>
): quotedSupplyPoint is z.infer<typeof ElectricityPaginatedQuotedSupplyPointSchema> {
    return quotedSupplyPoint.supplyPoint.marketName === MarketName.FRA_ELECTRICITY
}

function isGasSupplyPoint(
    quotedSupplyPoint: z.infer<typeof QuotedSupplyPointSchema>
): quotedSupplyPoint is z.infer<typeof GasPaginatedQuotedSupplyPointSchema> {
    return quotedSupplyPoint.supplyPoint.marketName === MarketName.FRA_GAS
}

const RequestQuoteMutationSchema = z.union([
    z.object({
        quoteRequestCode: z.null(),
    }),
    z
        .object({
            quoteRequestCode: z.string(),
            quotedSupplyPoints: getPaginatedNodesSchema(QuotedSupplyPointSchema),
        })
        .transform(({ quoteRequestCode, quotedSupplyPoints }) => {
            const electricityQuotedSupplyPoint = quotedSupplyPoints.find(isElectricitySupplyPoint)
            const gasQuotedSupplyPoint = quotedSupplyPoints.find(isGasSupplyPoint)

            return {
                quoteRequestCode,
                electricityQuotedSupplyPoint,
                gasQuotedSupplyPoint,
            }
        }),
])

const RequestQuoteMutation = graphql(`
    mutation requestQuote($input: RequestQuoteInput!, $firstQuotedProducts: Int!) {
        requestQuote(input: $input) {
            quoteRequestCode
            quotedSupplyPoints(first: 3) {
                edges {
                    node {
                        quotedSupplyPoint {
                            id
                        }
                        supplyPoint {
                            marketName
                        }
                        quotedProducts(first: $firstQuotedProducts) {
                            edges {
                                node {
                                    id
                                    monthlyAmount
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`)

export type RequestQuoteMutationInput = InputOf<typeof RequestQuoteMutation>

export function useRequestQuoteMutation() {
    const graphQLClient = useGraphQLClient()

    return useMutation({
        mutationKey: ["OnboardingRequestQuoteMutation"],
        mutationFn: async (input: RequestQuoteMutationInput) => {
            const data = await graphQLClient.request(RequestQuoteMutation, {
                firstQuotedProducts: 30,
                input,
            })

            try {
                return RequestQuoteMutationSchema.parse(data.requestQuote)
            } catch {
                throwKrakenErrorResponse({ errorCode: ERROR_CODE.QUOTE_FAILED })
            }
        },
    })
}
